import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Check, LogOut, MapPin, Monitor, Pencil, Smartphone, Tablet, X } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { DeviceSession } from '../../../common/hooks/use-devices-api.hook';

/**
 * Red outline for sign-out buttons, on top of the outline variant. Never on the
 * default variant: it is filled, and its text colour wins over a red one, so the
 * label only showed on hover.
 */
export const DESTRUCTIVE = 'border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive';

dayjs.extend(relativeTime);

const ACTIVE_NOW_MS = 2 * 60_000;

export function deviceTitle(s: DeviceSession, t: TFunction): string {
  if (s.label) return s.label;
  if (s.browser && s.os) return String(t('SEATS.DEVICE.BROWSER_ON_OS', { browser: s.browser, os: s.os }));
  return s.browser || s.os || String(t('SEATS.DEVICE.UNKNOWN'));
}

const DeviceIcon = ({ type, className }: { type?: string; className?: string }) =>
  type === 'mobile' ? <Smartphone className={className} /> : type === 'tablet' ? <Tablet className={className} /> : <Monitor className={className} />;

interface Props {
  session: DeviceSession;
  onRevoke: (s: DeviceSession) => void;
  /** Omit to hide renaming (support view). */
  onRename?: (s: DeviceSession, label: string) => Promise<void>;
  busy?: boolean;
}

/** One signed-in device: what it is, who uses it, where, and whether it holds a seat. */
export function DeviceCard({ session, onRevoke, onRename, busy }: Props) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(session.label || '');
  const activeNow = Date.now() - new Date(session.lastSeenAt).getTime() < ACTIVE_NOW_MS;
  const place = [session.location?.city, session.location?.country].filter(Boolean).join(', ');

  const saveLabel = async () => {
    if (!onRename) return;
    await onRename(session, label);
    setEditing(false);
  };

  return (
    <Card
      className="flex flex-col gap-4 p-5"
      data-testid="device-card"
    >
      <div className="flex items-start gap-3">
        <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-xl border', session.active ? 'bg-muted/60' : 'bg-transparent opacity-60')}>
          <DeviceIcon type={session.deviceType} className="size-5 text-muted-foreground" />
        </span>
        <div className="min-w-0 flex-1">
          {editing ? (
            <form
              className="flex items-center gap-1.5"
              onSubmit={(e) => { e.preventDefault(); saveLabel(); }}
            >
              <Input
                autoFocus
                value={label}
                maxLength={60}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={deviceTitle({ ...session, label: undefined }, t)}
                className="h-8"
                aria-label={t('SEATS.DEVICE.NAME_LABEL')}
              />
              <Button type="submit" size="icon-sm" variant="outline" aria-label={t('SEATS.DEVICE.SAVE_NAME')}><Check /></Button>
              <Button type="button" size="icon-sm" variant="ghost" aria-label={t('SEATS.DEVICE.CANCEL')} onClick={() => { setEditing(false); setLabel(session.label || ''); }}><X /></Button>
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <p className="truncate font-medium">{deviceTitle(session, t)}</p>
              {session.current && <Badge variant="secondary">{t('SEATS.DEVICE.THIS_DEVICE')}</Badge>}
              {onRename && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="rounded p-0.5 text-muted-foreground opacity-60 transition-opacity hover:opacity-100"
                  aria-label={t('SEATS.DEVICE.RENAME')}
                >
                  <Pencil className="size-3.5" />
                </button>
              )}
            </div>
          )}
          <p className="truncate text-sm text-muted-foreground">{session.email}</p>
        </div>
      </div>

      <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span
            className={cn('size-1.5 rounded-full', activeNow ? 'bg-success' : session.active ? 'bg-foreground/50' : 'bg-muted-foreground/30')}
          />
          {activeNow
            ? t('SEATS.DEVICE.ACTIVE_NOW')
            : session.active
              ? t('SEATS.DEVICE.ACTIVE_AGO', { when: dayjs(session.lastSeenAt).fromNow() })
              : t('SEATS.DEVICE.IDLE', { when: dayjs(session.lastSeenAt).fromNow() })}
        </span>
        {(place || session.ip) && (
          <span className="flex items-center gap-1.5">
            <MapPin className="size-3" />
            <span className="truncate">{[place, session.ip].filter(Boolean).join(' · ')}</span>
          </span>
        )}
        {session.createdAt && (
          <span>{t('SEATS.DEVICE.SIGNED_IN_ON', { date: dayjs(session.createdAt).format('D MMM YYYY, HH:mm') })}</span>
        )}
      </div>

      <div className="mt-auto flex justify-end">
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          className={DESTRUCTIVE}
          onClick={() => onRevoke(session)}
        >
          <LogOut data-icon="inline-start" />
          {session.current ? t('SEATS.DEVICE.SIGN_OUT_HERE') : t('SEATS.DEVICE.SIGN_OUT')}
        </Button>
      </div>
    </Card>
  );
}
